import React, { forwardRef } from 'react';
import { Search, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react';

/**
 * Smart Search Bar for EDI viewer
 * Provides search, filtering, and navigation for segments
 */
const SmartSearchBar = forwardRef(({
    searchQuery = '',
    onSearchChange,
    searchType = 'all',
    onSearchTypeChange,
    currentMatch = 0,
    totalMatches = 0,
    onPrevMatch,
    onNextMatch,
    changesFilter = 'all',
    onChangesFilterChange,
    showSchemaDriftOnly = false,
    onShowSchemaDriftOnlyChange,
    isCompareMode = true,
    isViewMode = false
}, ref) => {
    const searchTypes = [
        { value: 'all', label: 'All' },
        { value: 'segment', label: 'Segment' },
        { value: 'value', label: 'Value' }
    ];

    const changesOptions = [
        { value: 'all', label: 'All Changes' },
        { value: 'breaking', label: 'Breaking Only' },
        { value: 'added', label: 'Added Only' },
        { value: 'removed', label: 'Removed Only' },
        { value: 'changed', label: 'Modified Only' },
        { value: 'renamed', label: 'Renamed Only' },
    ];

    return (
        <div className="flex items-center gap-1.5">
            {/* Search Input */}
            <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                    ref={ref}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Search..."
                    className="pl-8 pr-7 py-1.5 w-40 text-xs bg-slate-700 border-0 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-slate-400"
                />
                {searchQuery && (
                    <button
                        onClick={() => onSearchChange('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#2d2d2d] shadow-sm border border-[#3c3c3c] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity hover:text-blue-400"
                        title="Clear search"
                    >
                        <X size={12} />
                    </button>
                )}
            </div>

            {/* Search Type Dropdown */}
            <select
                value={searchType}
                onChange={(e) => onSearchTypeChange(e.target.value)}
                className="px-2 py-1.5 text-xs bg-slate-700 border-0 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white cursor-pointer"
            >
                {searchTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                ))}
            </select>

            {/* Changes Filter (Compare mode only) */}
            {isCompareMode && !isViewMode && (
                <>
                    <select
                        value={changesFilter}
                        onChange={(e) => onChangesFilterChange(e.target.value)}
                        className="px-2 py-1.5 text-xs bg-slate-700 border-0 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white cursor-pointer"
                    >
                        {changesOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>

                    {/* Structural / All Changes Toggle */}
                    <div
                        className="flex items-center gap-2 ml-2 bg-slate-800/40 hover:bg-slate-800/60 px-2.5 py-1.5 rounded-md cursor-pointer select-none transition-colors border border-slate-700"
                        onClick={() => onShowSchemaDriftOnlyChange?.(!showSchemaDriftOnly)}
                    >
                        <span className={`text-[11px] whitespace-nowrap transition-colors ${!showSchemaDriftOnly ? 'text-white font-medium' : 'text-slate-400'}`}>
                            All Changes
                        </span>

                        <div className={`relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${showSchemaDriftOnly ? 'bg-blue-500' : 'bg-slate-500'}`}>
                            <span
                                className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200`}
                                style={{ transform: showSchemaDriftOnly ? 'translateX(18px)' : 'translateX(2px)' }}
                            />
                        </div>

                        <span className={`text-[11px] whitespace-nowrap transition-colors ${showSchemaDriftOnly ? 'text-white font-medium' : 'text-slate-400'}`}>
                            Structural Changes
                        </span>
                    </div>
                </>
            )}

            {/* Navigation */}
            {totalMatches > 0 && (
                <div className="flex items-center gap-0.5 bg-slate-700 rounded-md px-1">
                    <button
                        onClick={onPrevMatch}
                        disabled={currentMatch <= 1}
                        className="p-1 rounded hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Previous match"
                    >
                        <ChevronLeft size={14} className="text-slate-300" />
                    </button>
                    <span className="text-xs text-slate-300 min-w-[40px] text-center font-medium">
                        {currentMatch}/{totalMatches}
                    </span>
                    <button
                        onClick={onNextMatch}
                        disabled={currentMatch >= totalMatches}
                        className="p-1 rounded hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Next match"
                    >
                        <ChevronRight size={14} className="text-slate-300" />
                    </button>
                </div>
            )}
        </div>
    );
});

export default SmartSearchBar;
